<template>
  <ComponentHeader :heading-level="4" title="Entity Configuration" subtitle="Define entity types, manage their attributes, and configure display components">
      <button
        @click="showCreateModal = true"
        :disabled="displayDirty || displaySaving"
        class="btn-primary-sm"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        Create New Entity Type
      </button>
    </ComponentHeader>

  <div class="p-4">
    <div ref="splitPanels" class="flex items-start">
    <EntityTypeRelationshipGraph ref="relationshipGraph" v-if="entityTypes.length" :entity-types="entityTypes" :selected-type-id="selectedTypeId" class="min-w-0" :style="{ flex: `${graphPanelPercent} 1 0px` }" @select="selectEntityType" />
    <div
      v-if="entityTypes.length"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize entity relationships and entity details panels"
      :aria-valuenow="Math.round(graphPanelPercent)"
      :aria-valuetext="`Relationships ${Math.round(graphPanelPercent)}%, details ${Math.round(100 - graphPanelPercent)}%`"
      :aria-valuemin="25"
      :aria-valuemax="75"
      tabindex="0"
      class="group relative w-4 shrink-0 self-stretch cursor-col-resize touch-none select-none outline-none"
      @pointerdown.prevent="startPanelResize"
      @pointermove="resizePanels"
      @pointerup="endPanelResize"
      @pointercancel="endPanelResize"
      @lostpointercapture="endPanelResize"
      @keydown.left.prevent="setGraphPanelPercent(graphPanelPercent - 2)"
      @keydown.right.prevent="setGraphPanelPercent(graphPanelPercent + 2)"
      @keydown.home.prevent="setGraphPanelPercent(25)"
      @keydown.end.prevent="setGraphPanelPercent(75)"
    >
      <span class="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors group-hover:bg-primary-500 group-focus-visible:bg-primary-500" :class="resizingPanels ? 'bg-primary-500' : 'bg-gray-300'"></span>
      <span class="pointer-events-none absolute left-1/2 top-1/2 flex h-8 w-3 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded border bg-white text-gray-400 group-hover:border-primary-500 group-focus-visible:border-primary-500" :class="resizingPanels ? 'border-primary-500' : 'border-gray-300'">
        <svg class="h-4 w-2" viewBox="0 0 8 16" fill="currentColor" aria-hidden="true"><circle cx="4" cy="4" r="1" /><circle cx="4" cy="8" r="1" /><circle cx="4" cy="12" r="1" /></svg>
      </span>
    </div>

    <!-- Selected type detail panel -->
    <div class="relative min-w-0 self-stretch" :style="{ flex: `${100 - graphPanelPercent} 1 0px` }">
    <div class="absolute inset-0 flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div v-if="!selectedType" class="flex flex-1 items-center justify-center px-4 text-xs text-gray-500">
        Click an entity type in the graph to configure it.
      </div>
      <template v-else>
        <div class="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-4 py-3">
          <div>
            <h3 class="text-sm font-semibold text-gray-900">{{ selectedType.type_name }}</h3>
            <span class="text-xs text-gray-500">{{ getAttributeCount(selectedType.id) }} attributes · {{ getComponentCount(selectedType.display_config) }} display components</span>
          </div>
          <span
            :class="selectedType.is_active ? 'bg-secondary-100 text-gray-800' : 'bg-gray-100 text-gray-500'"
            class="px-2 inline-flex text-xs leading-5 font-medium rounded-full shrink-0"
          >{{ selectedType.is_active ? 'Active' : 'Inactive' }}</span>
        </div>
        <div class="flex shrink-0 items-center justify-between border-b border-gray-200 px-4">
          <div class="flex gap-0 text-xs">
            <button v-for="tab in detailTabs" :key="tab" type="button"
              class="px-3 py-2 border-b-2 transition-colors"
              :class="detailTab === tab ? 'border-primary-500 text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'"
              @click="selectDetailTab(tab)"
            >{{ tab }}</button>
          </div>
          <button type="button" class="px-2.5 py-1 rounded border border-red-300 text-red-600 text-xs hover:bg-red-50 transition-colors" @click="confirmDeleteType(selectedType)">Delete</button>
        </div>
        <div class="min-h-0 flex-1 px-4 py-3" :class="detailTab === 'Entities' ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'">
          <template v-if="detailTab === 'Entities'">
            <LoadingIndicator v-if="entitiesLoading" message="Loading entities…" role="status" aria-live="polite" />
            <p v-else-if="entitiesError" role="alert" class="px-3 py-4 text-xs text-gray-600">{{ entitiesError }}</p>
            <template v-else>
              <div class="flex shrink-0 items-center gap-2 mb-2">
                <input v-model="entitySearch" type="search" aria-label="Search entities by name or ID" placeholder="Search entities…" class="sensitive min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-neutral-500" />
                <span class="shrink-0 text-xs text-gray-500 tabular-nums">
                  <template v-if="entitySearch">{{ filteredEntities.length.toLocaleString() }} of </template>{{ typeEntities.length.toLocaleString() }} {{ typeEntities.length === 1 ? 'entity' : 'entities' }}
                </span>
                <button v-if="canCreateEntities" type="button" class="inline-flex shrink-0 items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50" @click="showCreateEntity = true">
                  <svg class="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" d="M10 4v12M4 10h12" /></svg>
                  Create
                </button>
              </div>
              <p v-if="entityActionError" role="alert" class="mb-2 text-xs text-red-700">{{ entityActionError }}</p>
              <div class="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200">
                <table class="w-full table-fixed text-xs leading-4">
                  <caption class="sr-only">Entities of the selected type</caption>
                  <colgroup><col style="width: 4%" /><col style="width: 38%" /><col style="width: 11%" /><col style="width: 23.5%" /><col style="width: 23.5%" /></colgroup>
                  <thead class="sticky top-0 z-10 bg-gray-50 text-gray-600">
                    <tr>
                      <th scope="col" class="bg-gray-50 border-b border-gray-200 px-2 py-2 text-left font-medium">
                        <input type="checkbox" class="rounded border-gray-300" :checked="allVisibleSelected" :indeterminate.prop="someVisibleSelected" aria-label="Select all visible entities" @change="toggleAllVisible" />
                      </th>
                      <th scope="col" class="bg-gray-50 border-b border-gray-200 px-2 py-2 text-left font-medium" :aria-sort="entitySortColumn === 'name' ? (entitySortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                        <button type="button" class="inline-flex items-center gap-1 text-[11px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500" aria-label="Sort by entity name" @click="sortEntitiesBy('name')">
                          Entity name <span aria-hidden="true" class="text-gray-400">{{ entitySortColumn === 'name' ? (entitySortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                        </button>
                      </th>
                      <th scope="col" class="bg-gray-50 border-b border-gray-200 px-2 py-2 text-right font-medium" :aria-sort="entitySortColumn === 'id' ? (entitySortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                        <button type="button" class="inline-flex items-center gap-1 text-[11px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500" aria-label="Sort by entity ID" @click="sortEntitiesBy('id')">
                          ID <span aria-hidden="true" class="text-gray-400">{{ entitySortColumn === 'id' ? (entitySortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                        </button>
                      </th>
                      <th v-for="column in entityDateColumns" :key="column.key" scope="col" class="bg-gray-50 border-b border-gray-200 px-2 py-2 text-left font-medium" :aria-sort="entitySortColumn === column.key ? (entitySortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                        <button type="button" class="inline-flex items-center gap-1 text-[11px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500" :aria-label="`Sort by ${column.label.toLowerCase()}`" @click="sortEntitiesBy(column.key)">
                          {{ column.label }} <span aria-hidden="true" class="text-gray-400">{{ entitySortColumn === column.key ? (entitySortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <template v-for="(entity, index) in filteredEntities" :key="entity.id">
                      <tr class="align-top" :class="index % 2 ? 'bg-gray-50' : 'bg-white'">
                        <td class="px-2 py-2 text-center">
                          <input type="checkbox" class="mt-0.5 rounded border-gray-300" :checked="selectedEntityIds.includes(entity.id)" :aria-label="`Select ${entity.name}`" @click.stop @change="toggleEntity(entity.id)" />
                        </td>
                        <td class="px-2 py-2 text-gray-800">
                          <button type="button" class="flex w-full items-start gap-2 rounded text-left text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500" :aria-expanded="expandedEntityId === entity.id" :aria-controls="`entity-details-${entity.id}`" @click="toggleEntityDetails(entity.id)">
                            <svg class="mt-0.5 h-3 w-3 shrink-0 text-gray-500 transition-transform" :class="{ 'rotate-90': expandedEntityId === entity.id }" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 3l5 5-5 5" /></svg>
                            <span class="sensitive min-w-0 break-words">{{ entity.name }}</span>
                          </button>
                        </td>
                        <td class="sensitive px-2 py-2 text-right font-mono text-[11px] tabular-nums text-gray-500">{{ entity.id }}</td>
                        <td v-for="column in entityDateColumns" :key="column.key" class="break-words px-2 py-2 text-[11px] text-gray-600">
                          <time :datetime="entity[column.key] || undefined">
                            <span class="block">{{ formatEntityDate(entity[column.key]) }}</span>
                            <span v-if="entity[column.key]" class="block text-gray-500">{{ formatEntityTime(entity[column.key]) }}</span>
                          </time>
                        </td>
                      </tr>
                      <tr v-if="expandedEntityId === entity.id">
                        <td colspan="5" class="bg-gray-50 p-3">
                          <div :id="`entity-details-${entity.id}`" class="rounded border border-gray-200 bg-white p-3">
                            <section class="mb-4">
                              <div class="mb-2 flex items-center gap-2">
                                <h4 class="text-[11px] font-semibold text-gray-700">Identifiers</h4>
                                <button v-if="canEditIdentifiers" type="button" class="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50" :aria-expanded="identifierEditorId === entity.id" @click="identifierEditorId = identifierEditorId === entity.id ? null : entity.id">{{ identifierEditorId === entity.id ? 'Hide editor' : 'Edit' }}</button>
                                <button v-if="canEditIdentifiers" type="button" class="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50" aria-label="Add identifier" @click="identifierEditorId = null; identifierAddEntity = entity">Add</button>
                                <button v-if="canDeleteEntities" type="button" class="ml-auto inline-flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-50" aria-label="Delete entity" title="Delete entity" :disabled="deletingEntityId !== null" @click="deleteListEntity(entity)">
                                  <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v5M14 11v5" /></svg>
                                </button>
                              </div>
                              <div class="border-l border-gray-200 pl-3">
                                <EntityIdentifiers v-if="identifierEditorId === entity.id && canEditIdentifiers" :key="entity.id" :entity-id="entity.id" :allow-add="false" hide-label @updated="handleIdentifiersUpdated" />
                                <dl v-else-if="entity.identifiers?.length" class="space-y-2 text-[11px]">
                                  <div v-for="identifier in entity.identifiers" :key="identifier.id" class="flex flex-wrap gap-x-2">
                                    <dt class="text-gray-500">{{ identifier.identifier_type_display }}</dt>
                                    <dd class="sensitive break-all font-mono text-gray-800">{{ identifier.identifier }}</dd>
                                  </div>
                                </dl>
                                <p v-else class="text-[11px] text-gray-500">No identifiers.</p>
                              </div>
                            </section>
                            <section>
                              <div class="mb-2 flex items-center gap-2">
                                <h4 class="text-[11px] font-semibold text-gray-700">Parents</h4>
                                <span class="text-[11px] tabular-nums text-gray-500">{{ entity.parent_count }}</span>
                              </div>
                              <div class="border-l border-gray-200 pl-3">
                                <FormManageEntityParents
                                  :key="`parents-${entity.id}`"
                                  :entity-id="entity.id"
                                  :entity-name="entity.name"
                                  inline
                                  :read-only="!canEditHierarchy"
                                  @loaded="entity.parent_count = $event.length"
                                  @updated="entity.parent_count = $event.length; $refs.relationshipGraph?.loadGraph()"
                                />
                              </div>
                            </section>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <tr v-if="!filteredEntities.length">
                      <td colspan="5" class="px-4 py-5 text-center text-gray-500">{{ entitySearch ? 'No entities match your search.' : 'No entities of this type.' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="selectedEntityIds.length" class="relative mt-2 flex shrink-0 items-center justify-between rounded border border-gray-200 bg-gray-50 px-2 py-2 text-[11px]">
                <span class="text-gray-600">{{ selectedEntityIds.length }} selected</span>
                <div class="relative">
                  <button type="button" class="bulk-button" aria-haspopup="menu" :aria-expanded="bulkMenuOpen" @click="bulkMenuOpen = !bulkMenuOpen">Bulk actions <span aria-hidden="true">⌄</span></button>
                  <div v-if="bulkMenuOpen" class="absolute bottom-full right-0 z-20 mb-1 w-40 overflow-hidden rounded border border-gray-200 bg-white shadow-lg" role="menu">
                    <button v-if="canEditHierarchy" type="button" class="bulk-menu-item" role="menuitem" @click="openBulkParentAction('add')">Add parent</button>
                    <button v-if="canEditHierarchy" type="button" class="bulk-menu-item" role="menuitem" @click="openBulkParentAction('remove')">Remove parent</button>
                    <button v-if="canDeleteEntities" type="button" class="bulk-menu-item text-red-600" role="menuitem" @click="bulkDeleteEntities">Delete</button>
                  </div>
                </div>
              </div>
            </template>
          </template>
          <template v-else-if="detailTab === 'Attributes'">
            <div class="flex items-center justify-between mb-3">
              <button type="button" class="px-2.5 py-1 rounded border border-gray-300 text-gray-700 text-xs hover:bg-gray-50 transition-colors" @click="manageAttributes(selectedType)">Edit attributes</button>
              <button
                v-if="hasTemplate(selectedType.type_name) && attributeCounts[selectedType.id] === 0"
                type="button" class="px-2.5 py-1 rounded border border-gray-300 text-gray-700 text-xs hover:bg-gray-50 transition-colors" @click="initializeFromTemplate(selectedType)"
              >Initialize from template</button>
            </div>
            <LoadingIndicator v-if="detailLoading" message="Loading attributes…" />
            <table v-else-if="detailAttributes.length" class="w-full table-fixed text-xs text-gray-700">
              <colgroup>
                <col style="width: 40%" />
                <col style="width: 20%" />
                <col style="width: 25%" />
                <col style="width: 15%" />
              </colgroup>
              <thead class="border-b border-gray-200 text-left">
                <tr>
                  <th class="px-2 py-2 font-medium text-gray-600">Field</th>
                  <th class="px-2 py-2 font-medium text-gray-600">Type</th>
                  <th class="px-2 py-2 font-medium text-gray-600">Group</th>
                  <th class="px-2 py-2 font-medium text-gray-600">Required</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="attr in detailAttributes" :key="attr.id" class="hover:bg-gray-50">
                  <td class="px-2 py-2 break-words">
                    <div class="font-medium text-gray-800">{{ attr.display_name }}</div>
                    <div class="text-gray-400 font-mono mt-0.5">{{ attr.name }}</div>
                  </td>
                  <td class="px-2 py-2 capitalize">{{ attr.field_type }}</td>
                  <td class="px-2 py-2 break-words">{{ attr.display_group || 'General' }}</td>
                  <td class="px-2 py-2">{{ attr.is_required ? 'Yes' : '\u2014' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="text-xs text-gray-500 py-4">No attributes configured.</p>
          </template>
          <template v-else-if="detailTab === 'Display'">
            <EntityDisplayEditor
              :key="selectedType.id"
              :entity-type="selectedType"
              :entity-types="entityTypes"
              :attributes="detailAttributes"
              @dirty-change="displayDirty = $event"
              @saving-change="displaySaving = $event"
              @saved="onDisplaySaved"
            />
          </template>
        </div>
      </template>
    </div>
    </div>
    </div>

    <AddEntityIdentifierModal
      v-if="identifierAddEntity && canEditIdentifiers"
      :key="identifierAddEntity.id"
      :entity="identifierAddEntity"
      @created="handleIdentifierCreated"
      @close="identifierAddEntity = null"
    />

    <BulkEntityParentActionModal
      v-if="bulkParentAction"
      :child-ids="selectedEntityIds"
      :action="bulkParentAction"
      @completed="refreshAfterBulkAction"
      @close="bulkParentAction = null"
    />

    <FormEntity
      v-if="showCreateEntity && selectedType && canCreateEntities"
      :key="selectedType.id"
      :is-visible="showCreateEntity"
      :entity-type-name="selectedType.type_name"
      :navigate-on-create="false"
      @close="showCreateEntity = false"
      @update="onListEntityCreated"
    />

    <!-- Manage Attributes Modal (existing - unchanged) -->
    <div
      v-if="showAttributesModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="closeAttributesModal"
    >
      <div class="relative top-10 mx-auto p-5 w-full max-w-4xl">
        <div class="bg-white rounded-lg shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
          <!-- Modal Header -->
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900">
              Manage Attributes - {{ editingEntityType?.type_name }}
            </h3>
            <button
              @click="closeAttributesModal"
              class="text-gray-400 hover:text-gray-500"
            >
              <font-awesome-icon :icon="['fas', 'times']" class="h-6 w-6" />
            </button>
          </div>

          <!-- Modal Body -->
          <div class="flex-1 overflow-y-auto p-6">
            <!-- Add Attribute Button -->
            <div class="mb-4 flex justify-between items-center">
              <h4 class="text-sm font-medium text-gray-700">Entity Attributes</h4>
              <button
                @click="showAddAttributeForm = true"
                class="btn-primary-sm"
              >
                <font-awesome-icon :icon="['fas', 'plus']" />
                Add Attribute
              </button>
            </div>

            <!-- Add/Edit Attribute Form -->
            <div v-if="showAddAttributeForm" class="mb-4 p-4 bg-gray-50 rounded-lg">
              <h5 class="text-sm font-semibold text-gray-700 mb-3">
                {{ editingAttribute ? 'Edit Attribute' : 'New Attribute' }}
              </h5>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
                  <input
                    v-model="newAttribute.name"
                    placeholder="e.g., phone_number"
                    :disabled="!!editingAttribute"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <input
                    v-model="newAttribute.display_name"
                    placeholder="e.g., Phone Number"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                  <select
                    v-model="newAttribute.field_type"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="boolean">Boolean</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="url">URL</option>
                    <option value="json">JSON</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Display Group</label>
                  <input
                    v-model="newAttribute.display_group"
                    placeholder="e.g., Contact Info"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    v-model="newAttribute.description"
                    placeholder="Brief description of this field"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                    rows="2"
                  />
                </div>
                <div class="col-span-2 flex items-center gap-4">
                  <label class="flex items-center">
                    <input
                      v-model="newAttribute.is_required"
                      type="checkbox"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark mr-2"
                    />
                    <span class="text-sm text-gray-700">Required Field</span>
                  </label>
                </div>
              </div>
              <div class="mt-4 flex justify-end gap-2">
                <button
                  @click="cancelAddAttribute"
                  class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  @click="saveAttribute"
                  class="btn-primary-sm"
                >
                  <font-awesome-icon :icon="['fas', 'save']" />
                  {{ editingAttribute ? 'Update' : 'Save' }} Attribute
                </button>
              </div>
            </div>

            <!-- Attributes List -->
            <div class="space-y-2">
              <div
                v-for="(attribute, index) in entityAttributes"
                :key="attribute.id"
                class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div class="flex-1">
                  <div class="flex items-center gap-3">
                    <span class="font-medium text-sm text-gray-900">{{ attribute.display_name }}</span>
                    <span class="text-xs text-gray-500 font-mono bg-gray-200 px-2 py-0.5 rounded">{{ attribute.name }}</span>
                    <span class="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                      {{ attribute.field_type }}
                    </span>
                    <span v-if="attribute.is_required" class="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                      Required
                    </span>
                  </div>
                  <div v-if="attribute.description" class="text-xs text-gray-500 mt-1">
                    {{ attribute.description }}
                  </div>
                </div>
                <div class="flex items-center gap-1">
                  <button
                    @click="moveAttribute(index, -1)"
                    :disabled="index === 0"
                    class="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    title="Move up"
                  >
                    <font-awesome-icon :icon="['fas', 'arrow-up']" class="text-xs" />
                  </button>
                  <button
                    @click="moveAttribute(index, 1)"
                    :disabled="index === entityAttributes.length - 1"
                    class="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    title="Move down"
                  >
                    <font-awesome-icon :icon="['fas', 'arrow-down']" class="text-xs" />
                  </button>
                  <button
                    @click="editAttribute(attribute)"
                    class="p-1.5 text-blue-600 hover:text-blue-800"
                    title="Edit"
                  >
                    <font-awesome-icon :icon="['fas', 'edit']" />
                  </button>
                  <button
                    @click="deleteAttribute(attribute)"
                    class="p-1.5 text-red-600 hover:text-red-800"
                    title="Delete"
                  >
                    <font-awesome-icon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Entity Type Modal (existing - unchanged) -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="showCreateModal = false"
    >
      <div class="relative top-20 mx-auto p-5 w-96">
        <div class="bg-white rounded-lg shadow-xl p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Create New Entity Type</h3>
          <input
            v-model="newEntityTypeName"
            placeholder="Entity type name (e.g., 'clinic', 'department')"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
            @keyup.enter="createEntityType"
          />
          <div class="mt-4 flex justify-end space-x-3">
            <button
              @click="showCreateModal = false"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="createEntityType"
              :disabled="!newEntityTypeName.trim()"
              class="btn-primary-sm"
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntityDisplayEditor from '@/components/Entity/EntityDisplayEditor.vue';
import EntityTypeRelationshipGraph from '@/components/Entity/EntityTypeRelationshipGraph.vue';
import abortable from '@/mixins/abortable';
import BulkEntityParentActionModal from '@/components/Entity/BulkEntityParentActionModal.vue';
import FormEntity from '@/components/forms/Form_Entity.vue';
import AddEntityIdentifierModal from '@/components/Entity/AddEntityIdentifierModal.vue';
import EntityIdentifiers from '@/components/Entity/EntityIdentifiers.vue';
import FormManageEntityParents from '@/components/Entity/Form_Manage_Entity_Parents.vue';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faTimes, faPlus, faSave, faEdit, faTrash, faToggleOn, faToggleOff,
  faArrowUp, faArrowDown, faInbox, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { isDemoMode } from '@/utils/demoSpoof';

library.add(
  faTimes, faPlus, faSave, faEdit, faTrash, faToggleOn, faToggleOff,
  faArrowUp, faArrowDown, faInbox, faExclamationTriangle
);

const entityDateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const entityTimeFormatter = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

export default {
  name: 'EntityTypeConfiguration',
  components: {
    FontAwesomeIcon,
    ComponentHeader,
    LoadingIndicator,
    FormManageEntityParents,
    EntityIdentifiers,
    AddEntityIdentifierModal,
    FormEntity,
    BulkEntityParentActionModal,
    EntityTypeRelationshipGraph,
    EntityDisplayEditor
  },
  mixins: [abortable],
  data() {
    return {
      entityTypes: [],
      graphPanelPercent: 100 * 7 / 12,
      resizingPanels: false,
      selectedTypeId: null,
      selectedEntityIds: [],
      bulkMenuOpen: false,
      bulkParentAction: null,
      bulkActionBusy: false,
      displayDirty: false,
      displaySaving: false,
      identifierEditorId: null,
      identifierAddEntity: null,
      expandedEntityId: null,
      showCreateEntity: false,
      deletingEntityId: null,
      entityActionError: '',
      detailAttributes: [],
      detailLoading: false,
      detailTab: 'Entities',
      entitySearch: '',
      entitySortColumn: 'name',
      entitySortDirection: 'asc',
      entityDateColumns: [{ key: 'created_at', label: 'Created at' }, { key: 'updated_at', label: 'Updated at' }],
      typeEntities: [],
      entitiesLoading: false,
      entitiesError: '',
      detailTabs: ['Entities', 'Attributes', 'Display'],
      entityAttributes: [],
      showAttributesModal: false,
      showCreateModal: false,
      showAddAttributeForm: false,
      editingEntityType: null,
      editingAttribute: null,
      newEntityTypeName: '',
      newAttribute: {
        name: '',
        display_name: '',
        description: '',
        field_type: 'text',
        is_required: false,
        display_group: '',
        display_order: 0
      },

      attributeCounts: {},
    };
  },
  computed: {
    canCreateEntities() {
      return canAccessFeature(FEATURES.ACTION_EDIT);
    },
    canDeleteEntities() {
      return canAccessFeature(FEATURES.ACTION_ENTITY_DELETE);
    },
    canEditIdentifiers() {
      return canAccessFeature(FEATURES.ACTION_EDIT);
    },
    canEditHierarchy() {
      return canAccessFeature(FEATURES.ACTION_HIERARCHY_CONFIG);
    },
    visibleEntityIds() { return this.filteredEntities.map(entity => entity.id); },
    allVisibleSelected() { return this.visibleEntityIds.length > 0 && this.visibleEntityIds.every(id => this.selectedEntityIds.includes(id)); },
    someVisibleSelected() { return this.visibleEntityIds.some(id => this.selectedEntityIds.includes(id)) && !this.allVisibleSelected; },
    filteredEntities() {
      const query = this.entitySearch.toLowerCase().trim();
      const rows = this.typeEntities.filter(entity => !query || (entity.name || '').toLowerCase().includes(query) || String(entity.id).includes(query));
      const direction = this.entitySortDirection === 'asc' ? 1 : -1;
      return rows.sort((a, b) => {
        const column = this.entitySortColumn;
        if (a[column] == null || b[column] == null) {
          if (a[column] == null && b[column] == null) return a.id - b.id;
          return a[column] == null ? 1 : -1;
        }
        const comparison = column === 'name'
          ? a.name.localeCompare(b.name)
          : ['id', 'parent_count'].includes(column) ? a[column] - b[column] : Date.parse(a[column]) - Date.parse(b[column]);
        return direction * comparison || a.id - b.id;
      });
    },
    selectedType() {
      return this.entityTypes.find(t => t.id === this.selectedTypeId) || null;
    },
    tenantSlug() {
      return this.$store.getters['tenant/current_tenant_slug'];
    },
  },
  watch: {
    selectedTypeId: {
      immediate: true,
      handler(id) {
        if (!id) { this.detailAttributes = []; return; }
        this.detailTab = 'Entities';
        this.identifierEditorId = null;
        this.identifierAddEntity = null;
        this.expandedEntityId = null;
        this.entityActionError = '';
        this.showCreateEntity = false;
        this.loadTypeEntities(id);
        this.loadDetailAttributes(id);
      },
    },
    tenantSlug() { this.fetchEntityTypes(); },
  },
  mounted() {
    this.fetchEntityTypes();
  },
  beforeUnmount() {
    this.endPanelResize();
  },
  methods: {
    canLeaveDisplay() {
      if (this.displaySaving) return false;
      if (this.displayDirty && !window.confirm('Discard unsaved display changes?')) return false;
      this.displayDirty = false;
      return true;
    },
    selectEntityType(id) {
      if (id === this.selectedTypeId || !this.canLeaveDisplay()) return;
      this.selectedTypeId = id;
    },
    selectDetailTab(tab) {
      if (tab === this.detailTab || !this.canLeaveDisplay()) return;
      this.detailTab = tab;
    },
    onDisplaySaved({ id, display_config }) {
      if (id !== this.selectedTypeId) return;
      this.entityTypes = this.entityTypes.map(type => type.id === id ? { ...type, display_config } : type);
      this.displayDirty = false;
    },
    setGraphPanelPercent(value) {
      this.graphPanelPercent = Math.max(25, Math.min(75, value));
    },
    startPanelResize(event) {
      if (event.button !== 0 || this.resizingPanels) return;
      this._panelResize = {
        pointerId: event.pointerId,
        target: event.currentTarget,
        startX: event.clientX,
        startPercent: this.graphPanelPercent,
        cursor: document.body.style.cursor,
        userSelect: document.body.style.userSelect,
      };
      this.resizingPanels = true;
      event.currentTarget.focus();
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    resizePanels(event) {
      const state = this._panelResize;
      if (!state || event.pointerId !== state.pointerId) return;
      const width = this.$refs.splitPanels.clientWidth - state.target.offsetWidth;
      if (width <= 0) return;
      this.setGraphPanelPercent(state.startPercent + (event.clientX - state.startX) / width * 100);
    },
    endPanelResize(event) {
      const state = this._panelResize;
      if (!state || (event && event.pointerId !== state.pointerId)) return;
      this._panelResize = null;
      this.resizingPanels = false;
      document.body.style.cursor = state.cursor;
      document.body.style.userSelect = state.userSelect;
      if (state.target.hasPointerCapture(state.pointerId)) state.target.releasePointerCapture(state.pointerId);
    },
    async confirmDeleteType(entityType) {
      if (this.displaySaving) return;
      if (!window.confirm(`Delete entity type "${entityType.type_name}"? This cannot be undone.`)) return;
      try {
        await axios.delete(`/api/entity-types/${entityType.id}/`);
        this.selectedTypeId = null;
        this.detailAttributes = [];
        await this.fetchEntityTypes();
      } catch (error) {
        if (this._isAborted(error)) return;
        const msg = error.response?.data?.detail || error.response?.data?.error || 'Failed to delete entity type.';
        window.alert(msg);
      }
    },
    formatEntityDate(value) {
      const date = value ? new Date(value) : null;
      return date && !Number.isNaN(date.getTime()) ? entityDateFormatter.format(date) : '—';
    },
    formatEntityTime(value) {
      const date = value ? new Date(value) : null;
      return date && !Number.isNaN(date.getTime()) ? entityTimeFormatter.format(date) : '—';
    },
    sortEntitiesBy(column) {
      this.entitySortDirection = this.entitySortColumn === column && this.entitySortDirection === 'asc' ? 'desc' : 'asc';
      this.entitySortColumn = column;
    },
    toggleEntity(entityId) {
      this.selectedEntityIds = this.selectedEntityIds.includes(entityId)
        ? this.selectedEntityIds.filter(id => id !== entityId)
        : [...this.selectedEntityIds, entityId];
    },
    toggleAllVisible() {
      this.selectedEntityIds = this.allVisibleSelected
        ? this.selectedEntityIds.filter(id => !this.visibleEntityIds.includes(id))
        : [...new Set([...this.selectedEntityIds, ...this.visibleEntityIds])];
    },
    openBulkParentAction(action) {
      this.bulkMenuOpen = false;
      this.bulkParentAction = action;
    },
    async refreshAfterBulkAction() {
      this.bulkParentAction = null;
      this.selectedEntityIds = [];
      await this.refreshAfterParentChange();
    },
    async bulkDeleteEntities() {
      this.bulkMenuOpen = false;
      if (!this.canDeleteEntities || !this.selectedEntityIds.length) return;
      const count = this.selectedEntityIds.length;
      if (!window.confirm(`Delete ${count} selected entit${count === 1 ? 'y' : 'ies'}? This cannot be undone.`)) return;
      this.bulkActionBusy = true;
      this.entityActionError = '';
      try {
        await axios.post('/api/entities/bulk-delete/', { entity_ids: this.selectedEntityIds }, { params: { tenant_slug: this.tenantSlug } });
        this.selectedEntityIds = [];
        await this.refreshAfterParentChange();
      } catch (error) {
        this.entityActionError = error.response?.data?.error || error.response?.data?.detail || 'Could not delete selected entities.';
      } finally {
        this.bulkActionBusy = false;
      }
    },
    toggleEntityDetails(entityId) {
      this.expandedEntityId = this.expandedEntityId === entityId ? null : entityId;
      this.identifierEditorId = null;
    },
    handleIdentifierCreated(identifier) {
      const entity = this.typeEntities.find(row => row.id === identifier.entity);
      if (entity) this.handleIdentifiersUpdated({
        entityId: entity.id,
        identifiers: [...(entity.identifiers || []), identifier],
      });
    },
    handleIdentifiersUpdated({ entityId, identifiers }) {
      this.typeEntities = this.typeEntities.map(entity => entity.id === Number(entityId) ? { ...entity, identifiers } : entity);
      this.$refs.relationshipGraph?.loadGraph();
    },
    async onListEntityCreated() {
      this.showCreateEntity = false;
      await this.refreshAfterParentChange();
    },
    async deleteListEntity(entity) {
      if (!this.canDeleteEntities || this.deletingEntityId !== null) return;
      // A native dialog is outside the Demo Mode mask: no entity name while on.
      const label = isDemoMode() ? 'this entity' : `"${entity.name}"`;
      if (!window.confirm(`Delete ${label}? This permanently deletes the entity and its attached records, including identifiers and attributes. Its parent and child links are removed, but child entities are not deleted. This cannot be undone.`)) return;
      const tenantSlug = this.tenantSlug;
      this.deletingEntityId = entity.id;
      this.entityActionError = '';
      try {
        await axios.delete(`/api/entities/${entity.id}/`, { params: { tenant_slug: tenantSlug } });
        if (this.tenantSlug !== tenantSlug) return;
        if (this.identifierEditorId === entity.id) this.identifierEditorId = null;
        if (this.expandedEntityId === entity.id) this.expandedEntityId = null;
        await this.refreshAfterParentChange();
      } catch (error) {
        if (this.tenantSlug === tenantSlug) this.entityActionError = error.response?.data?.detail || error.response?.data?.error || 'Could not delete entity.';
      } finally {
        this.deletingEntityId = null;
      }
    },
    async refreshAfterParentChange() {
      await Promise.all([
        this.selectedTypeId ? this.loadTypeEntities(this.selectedTypeId, { preserveSearch: true }) : Promise.resolve(),
        this.$refs.relationshipGraph?.loadGraph(),
      ]);
    },
    async loadTypeEntities(typeId, { preserveSearch = false } = {}) {
      const signal = this._renewAbortSignal('type-entities');
      this.entitiesLoading = true;
      this.entitiesError = '';
      this.typeEntities = [];
      if (!preserveSearch) this.entitySearch = '';
      try {
        const { data } = await axios.get(`/api/entity-types/${typeId}/entities/`, {
          signal,
          params: { tenant_slug: this.tenantSlug },
        });
        if (!signal.aborted && this.selectedTypeId === typeId) this.typeEntities = data;
      } catch (error) {
        if (this._isAborted(error)) return;
        if (this.selectedTypeId === typeId) this.entitiesError = 'Could not load entities.';
      } finally {
        if (!signal.aborted && this.selectedTypeId === typeId) this.entitiesLoading = false;
      }
    },
    async loadDetailAttributes(typeId) {
      this.detailLoading = true;
      this.detailAttributes = [];
      try {
        const { data } = await axios.get('/api/entity-attributes/', { params: { entity_type_id: typeId, show_inactive: true } });
        if (this.selectedTypeId === typeId) this.detailAttributes = data.sort((a, b) => a.display_order - b.display_order);
      } catch (e) {
        if (this._isAborted(e)) return;
      } finally {
        if (this.selectedTypeId === typeId) this.detailLoading = false;
      }
    },
    async fetchEntityTypes() {
      const signal = this._renewAbortSignal('entity-types');
      this.displayDirty = false;
      this.displaySaving = false;
      this._renewAbortSignal('type-entities');
      this.selectedTypeId = null;
      this.identifierEditorId = null;
      this.identifierAddEntity = null;
      this.expandedEntityId = null;
      this.showCreateEntity = false;
      this.entityActionError = '';
      this.typeEntities = [];
      this.entitiesLoading = false;
      this.entitiesError = '';
      this.entityTypes = [];
      this.attributeCounts = {};
      try {
        const response = await axios.get('/api/entity-types/', { signal });
        if (signal.aborted) return;
        this.entityTypes = response.data;

        for (const entityType of this.entityTypes) {
          if (signal.aborted) return;
          await this.fetchAttributeCount(entityType.id, signal);
        }
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Error fetching entity types:', error);
      }
    },

    async fetchAttributeCount(entityTypeId, signal) {
      try {
        const response = await axios.get('/api/entity-attributes/', {
          params: { entity_type_id: entityTypeId },
          signal,
        });
        if (signal?.aborted) return;
        this.attributeCounts[entityTypeId] = response.data.length;
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Error fetching attribute count:', error);
        this.attributeCounts[entityTypeId] = 0;
      }
    },

    async fetchAttributes(entityTypeId) {
      try {
        const response = await axios.get('/api/entity-attributes/', {
          params: {
            entity_type_id: entityTypeId,
            show_inactive: true
          }
        });
        this.entityAttributes = response.data.sort((a, b) => a.display_order - b.display_order);
      } catch (error) {
        console.error('Error fetching attributes:', error);
        this.entityAttributes = [];
      }
    },

    getAttributeCount(entityTypeId) {
      return this.attributeCounts[entityTypeId] || 0;
    },

    getTableColumnCount(displayConfig) {
      return displayConfig?.table_config?.columns?.length || 0;
    },

    getComponentCount(displayConfig) {
      if (!displayConfig || !displayConfig.layout_config || !displayConfig.layout_config.tabs) return 0;

      let count = 0;
      Object.values(displayConfig.layout_config.tabs).forEach(tabComponents => {
        count += tabComponents.length;
      });
      return count;
    },

    hasTemplate(typeName) {
      return ['practice', 'location', 'staff'].includes(typeName.toLowerCase());
    },

    async manageAttributes(entityType) {
      this.editingEntityType = entityType;
      await this.fetchAttributes(entityType.id);
      this.showAttributesModal = true;
    },

    closeAttributesModal() {
      this.showAttributesModal = false;
      this.showAddAttributeForm = false;
      this.editingEntityType = null;
      this.editingAttribute = null;
      this.resetNewAttribute();
    },

    resetNewAttribute() {
      this.newAttribute = {
        name: '',
        display_name: '',
        description: '',
        field_type: 'text',
        is_required: false,
        display_group: '',
        display_order: this.entityAttributes.length
      };
    },

    cancelAddAttribute() {
      this.showAddAttributeForm = false;
      this.editingAttribute = null;
      this.resetNewAttribute();
    },

    async saveAttribute() {
      if (!this.newAttribute.name || !this.newAttribute.display_name) {
        alert('Field name and display name are required');
        return;
      }

      try {
        const data = {
          ...this.newAttribute,
          entity_type: this.editingEntityType.id,
          display_order: this.editingAttribute ? this.newAttribute.display_order : this.entityAttributes.length
        };

        if (this.editingAttribute) {
          await axios.patch(`/api/entity-attributes/${this.editingAttribute.id}/`, data);
        } else {
          await axios.post('/api/entity-attributes/', data);
        }

        await this.fetchAttributes(this.editingEntityType.id);
        await this.fetchAttributeCount(this.editingEntityType.id);
        this.showAddAttributeForm = false;
        this.editingAttribute = null;
        this.resetNewAttribute();
      } catch (error) {
        console.error('Error saving attribute:', error);
        alert('Failed to save attribute');
      }
    },

    editAttribute(attribute) {
      this.editingAttribute = attribute;
      this.newAttribute = {
        name: attribute.name,
        display_name: attribute.display_name,
        description: attribute.description || '',
        field_type: attribute.field_type,
        is_required: attribute.is_required,
        display_group: attribute.display_group || '',
        display_order: attribute.display_order
      };
      this.showAddAttributeForm = true;
    },

    async deleteAttribute(attribute) {
      if (!confirm(`Delete attribute "${attribute.display_name}"?`)) return;

      try {
        await axios.delete(`/api/entity-attributes/${attribute.id}/`);
        await this.fetchAttributes(this.editingEntityType.id);
        await this.fetchAttributeCount(this.editingEntityType.id);
      } catch (error) {
        console.error('Error deleting attribute:', error);
        alert('Failed to delete attribute');
      }
    },

    async moveAttribute(index, direction) {
      const attributes = [...this.entityAttributes];
      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= attributes.length) return;

      [attributes[index], attributes[targetIndex]] = [attributes[targetIndex], attributes[index]];

      const updates = attributes.map((attr, idx) => ({
        id: attr.id,
        display_order: idx
      }));

      try {
        await axios.post('/api/entity-attributes/reorder/', { updates });
        this.entityAttributes = attributes;
      } catch (error) {
        console.error('Error reordering attributes:', error);
      }
    },

    async createEntityType() {
      if (!this.newEntityTypeName.trim()) return;

      try {
        await axios.post('/api/entity-types/', {
          type_name: this.newEntityTypeName,
          display_config: {
            layout_config: {
              tabs: {
                'Overview': [
                  { component: 'basic_info', order: 0 }
                ]
              }
            },
            table_config: {
              columns: []
            }
          },
          is_active: true
        });

        this.newEntityTypeName = '';
        this.showCreateModal = false;
        await this.fetchEntityTypes();
      } catch (error) {
        console.error('Error creating entity type:', error);
        alert('Failed to create entity type');
      }
    },

    async initializeFromTemplate(entityType) {
      try {
        await axios.post('/api/entity-types/initialize-from-template/', {
          type_name: entityType.type_name
        });

        await axios.post('/api/entity-attributes/initialize_from_schema/', {
          entity_type_id: entityType.id
        });

        await this.fetchEntityTypes();
        await this.fetchAttributeCount(entityType.id);
        alert(`Initialized ${entityType.type_name} from template`);
      } catch (error) {
        console.error('Error initializing from template:', error);
        alert('Failed to initialize from template');
      }
    }
  }
};
</script>

<style scoped>
@reference "../../assets/theme.css";
.bulk-button { @apply inline-flex h-6 items-center gap-1 rounded border border-gray-300 bg-white px-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500; }
.bulk-menu-item { @apply block w-full px-3 py-2 text-left text-[11px] text-gray-700 hover:bg-gray-50; }
</style>
